import { getClassNames } from '@websolutespa/bom-core';
import { useAutosizeTextArea, useLabel } from '@websolutespa/bom-mixer-hooks';
import { useLlm, useLlmView } from '@websolutespa/bom-mixer-llm';
import { MicIcon, PaperclipIcon, SendHorizonalIcon, SquareIcon, Volume2Icon, VolumeXIcon, XIcon } from 'lucide-react';
import { useRef } from 'react';
import style from './prompt.module.scss';
import { Suggestions } from './suggestions';

export const Prompt: React.FC = ({
  suggestions,
}: {
  suggestions?: boolean,
}) => {
  const label = useLabel();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const {
    BusyIndicator,
  } = useLlm(state => state.components);
  const app = useLlm(state => state.app);
  const threadId = useLlm(state => state.threadId);
  const prompt = useLlm(state => state.prompt);
  const upload = useLlm(state => state.upload);
  const messages = useLlm(state => state.messages);
  const streaming = useLlm(state => state.streaming);
  const speakEnabled = useLlm(state => state.speakEnabled);
  const { setPrompt, recognizeStart, recognizeStop, abort, toggleSpeak, removeFile, hasSpeechRecognitionSupport, hasSpeechSynthesisSupport, addFile, hasUploadSupport } = useLlm(state => state.actions);
  const opened = useLlmView(state => state.opened);
  const { open, send } = useLlmView(state => state.actions);

  useAutosizeTextArea(textAreaRef.current, prompt);

  const onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const prompt = event.target?.value;
    setPrompt(prompt);
  };

  const onSubmit = async (event?: React.FormEvent) => {
    event && event.preventDefault();
    if (!prompt) {
      return;
    }
    if (!opened) {
      open();
    }
    await send(prompt, (response) => {
      // console.log('Prompt.onChunk', response.chunks);
    }, (response) => {
      // console.log('Prompt.onEnd', response);
    });
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.code == 'Enter' && !event.shiftKey) {
      onSubmit(event);
    }
  };

  const hasSpeechRecognition = hasSpeechRecognitionSupport();
  const hasSpeechSynthesis = hasSpeechSynthesisSupport();
  const hasUpload = hasUploadSupport();

  return (
    <>
      <section className={style.prompt} id="llm-prompt">
        <form className={style.form} onSubmit={onSubmit}>
          <div className={getClassNames(style.control, {
            [style.hasSpeechRecognition]: hasSpeechRecognition,
            [style.hasSpeechSynthesis]: hasSpeechSynthesis,
            [style.hasUpload]: hasUpload,
            [style.isStreaming]: streaming,
          })}>
            {hasSpeechRecognition && (
              <button className={style.microphone} type="button"
                aria-label={label('llm.dictate')}
                onMouseDown={() => recognizeStart()}
                onMouseUp={() => recognizeStop()}
                onTouchStart={() => recognizeStart()}
                onTouchEnd={() => recognizeStop()}
              >
                <MicIcon />
              </button>
            )}
            <div className={style.textareaGroup}>
              <label className={style.label} htmlFor="llm-prompt-textarea">{label('llm.prompt')}</label>
              <textarea className={style.textarea} id="llm-prompt-textarea" name="prompt" placeholder={app?.contents.promptPlaceholder} onKeyDown={onKeyDown} onChange={onChange} ref={textAreaRef} rows={1} value={prompt} aria-disabled={streaming}></textarea>
              <BusyIndicator />
            </div>
            {hasUpload && (
              <div className={style.uploadGroup}>
                <div className={style.upload}
                  aria-label={label('llm.upload')}
                >
                  <PaperclipIcon />
                </div>
                <input className={style.uploadInput} type="file" id="file-input" accept=".jpg, .jpeg, .png, .webp, .gif, .bmp, .tif, .tiff, .txt, .rtf, .csv, .doc, .docx, .pdf" onChange={addFile} />
              </div>
            )}
            {hasUpload && upload && (
              <button className={style.uploadFile} onClick={() => removeFile()} title={upload.file.name}>
                <span>{upload.file.name}</span>
                <XIcon />
                <img src={upload.base64} alt="" />
              </button>
            )}
            {hasSpeechSynthesis && (
              <button className={style.speak} type="button"
                aria-label={speakEnabled ? label('llm.disableSpeak') : label('llm.enableSpeak')}
                aria-pressed={speakEnabled}
                onClick={toggleSpeak}
              >
                {speakEnabled ? <Volume2Icon /> : <VolumeXIcon />}
              </button>
            )}
            {streaming ? (
              <button className={style.stop} type="button"
                disabled={!threadId}
                aria-disabled={!threadId}
                aria-label={label('llm.stopResponse')}
                onClick={abort}
              >
                <i><SquareIcon /></i>
              </button>
            ) : (
              <button className={style.submit} type="submit"
                aria-disabled={!prompt}
                aria-label={label('llm.sendPrompt')}
              >
                <i><SendHorizonalIcon /></i>
              </button>
            )}
          </div>
        </form>
      </section>
      {suggestions && (
        <Suggestions />
      )}
    </>
  );
};
